<template>
  <div class="availability-info-wrapper">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        class="availability-info-wrapper"
        :show-close-button="false"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle>{{ $t("availability-info.title") }}</SDialogTitle>
        </SDialogHeader>
        <p>{{ $t("availability-info.description") }}</p>
        <div v-if="noAvailabilityInfo && noAvailabilityInfo.length">
          <div
            v-for="info in noAvailabilityInfo"
            :key="info.orderLineIds[0]"
            class="info-row"
          >
            <p>
              <strong>{{ info.categoryName }} - {{ info.priceName }}</strong>
            </p>
            <p>
              {{ $t("availability-info.available") }}
              {{ info.dbAvailability }}
            </p>
            <p>{{ $t("availability-info.booked") }} {{ info.bookedQty }}</p>
          </div>
        </div>
        <SDialogFooter class="sm:justify-center">
          <SButton type="button" size="lg" @click="removalConfirmation()">
            {{ $t("availability-info.ok-button") }}
          </SButton>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useOrderStore } from "@smeetz/order-store";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { ModalStatus } from "@/constants/modal";
import { BookingStep } from "@/constants/booking";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { removeOrderLines } from "@/utils/vue-helpers/orders.helper";
import { uncoveredAvailability } from "@/utils/vue-helpers/order-stock.helper";
import { LogLevel, LogService } from "@/services/logging";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "AvailabilityInfo",
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
  },
  setup() {
    const dialogVisible = dialogVisibleForStatus(ModalStatus.AvailabilityInfo);
    const noAvailabilityInfo = computed(() => uncoveredAvailability.value);

    return { dialogVisible, noAvailabilityInfo };
  },
  mounted() {
    if (!this.noAvailabilityInfo || !this.noAvailabilityInfo.length)
      this.closeModal();
  },
  methods: {
    closeModal() {
      this.dialogVisible = false;
    },
    /**
     * Removes every uncovered group's lines (addon children included), then
     * returns to the Order step when nothing is left on the session.
     */
    async removalConfirmation() {
      const orderLineIds = [
        ...new Set(
          this.noAvailabilityInfo.flatMap((info) => info.orderLineIds)
        ),
      ];
      try {
        await removeOrderLines(orderLineIds);
      } catch (error) {
        LogService.info(
          "Error removing uncovered order lines",
          {
            orderLineIds,
            error,
          },
          LogLevel.Error
        );
      }
      uncoveredAvailability.value = [];

      const session = useOrderStore().selectedOrder?.orderSession;
      if (!(session?.orderLines ?? []).length) {
        useOrderMetaStore().setStep(BookingStep.Order);
      }
      this.closeModal();
    },
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.availability-info-wrapper {
  p {
    font-size: 15px;
    word-break: normal;
  }
  .info-row {
    p {
      font-size: 13px;
      letter-spacing: 1px;
    }
  }
}
</style>
